import { useEffect } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';

import { type ProductKeys } from '../../../common/constants/products';

const FEDERATED_EMAIL_RESULT_PAGE_SIZE = 30;

type FederatedEmailResultCount = { count: number | null; hasMore: boolean };

function getFederatedEmailResultCount({
	rawCount,
	exactCount,
	hasNextPage,
	pageSize = FEDERATED_EMAIL_RESULT_PAGE_SIZE,
}: {
	rawCount: number;
	exactCount: number;
	hasNextPage: boolean;
	pageSize?: number;
}): FederatedEmailResultCount {
	if (rawCount === 0 && exactCount === 0) {
		return { count: null, hasMore: false };
	}

	if (!hasNextPage) {
		return { count: exactCount, hasMore: false };
	}

	return {
		count: Math.ceil(rawCount / pageSize) * pageSize,
		hasMore: true,
	};
}

type FederatedEmailProduct =
	| (typeof ProductKeys3P)['Gmail']
	| (typeof ProductKeys3P)['OutlookMail'];

const isFederatedEmailProduct = (
	productKey: ProductKeys | undefined,
): productKey is FederatedEmailProduct =>
	productKey === ProductKeys3P.Gmail || productKey === ProductKeys3P.OutlookMail;

export type FederatedEmailPageInfo = {
	endCursor?: string | null;
	hasNextPage?: boolean;
};

type UseFederatedEmailResultCountProps = {
	selectedProduct: ProductKeys | undefined;
	rawResultsLength: number;
	resultsLength: number;
	pageInfo?: FederatedEmailPageInfo | null;
	isLoadingNext: boolean;
	setFederatedGmailCount?: (count: number | null) => void;
	setHasMoreGmailResults?: (hasMore: boolean) => void;
	setFederatedOutlookMailCount?: (count: number | null) => void;
	setHasMoreOutlookMailResults?: (hasMore: boolean) => void;
};

export function useFederatedEmailResultCount({
	selectedProduct,
	rawResultsLength,
	resultsLength,
	pageInfo,
	isLoadingNext,
	setFederatedGmailCount,
	setHasMoreGmailResults,
	setFederatedOutlookMailCount,
	setHasMoreOutlookMailResults,
}: UseFederatedEmailResultCountProps): void {
	useEffect(() => {
		const hasNextPage = pageInfo?.hasNextPage === true;

		if (!isFederatedEmailProduct(selectedProduct)) {
			setFederatedGmailCount?.(null);
			setHasMoreGmailResults?.(false);
			setFederatedOutlookMailCount?.(null);
			setHasMoreOutlookMailResults?.(false);
			return;
		}

		const isFederatedEmailCountEnabled =
			selectedProduct === ProductKeys3P.Gmail
				? fg('formula_3p_gmail_result_count')
				: fg('formula_3p_outlook_result_count');

		const setSelectedProductCount =
			selectedProduct === ProductKeys3P.Gmail
				? setFederatedGmailCount
				: setFederatedOutlookMailCount;
		const setSelectedProductHasMore =
			selectedProduct === ProductKeys3P.Gmail
				? setHasMoreGmailResults
				: setHasMoreOutlookMailResults;

		if (!setSelectedProductCount) {
			return;
		}

		if (!isFederatedEmailCountEnabled) {
			if (selectedProduct === ProductKeys3P.Gmail) {
				setSelectedProductCount(null);
				setSelectedProductHasMore?.(false);
			}
			return;
		}

		const { count, hasMore } = getFederatedEmailResultCount({
			rawCount: rawResultsLength,
			exactCount: resultsLength,
			hasNextPage,
		});

		setSelectedProductCount(count);
		setSelectedProductHasMore?.(hasMore);
	}, [
		isLoadingNext,
		pageInfo,
		rawResultsLength,
		resultsLength,
		selectedProduct,
		setFederatedGmailCount,
		setHasMoreGmailResults,
		setFederatedOutlookMailCount,
		setHasMoreOutlookMailResults,
	]);
}
